import React, {
  useEffect,
  useState,
  useCallback,
  useMemo,
} from "react";
import { Server, RefreshCw } from "lucide-react";
import "./ServidoresCmd.css";

import statusServer, { forcarVerificacao } from "../../API/status/Status";
import ServersGrid from "../../features/servidoresCmd/ServersGrid/ServersGrid";
import PageHeader from "../../components/ui/PageHeader/PageHeader";
import ListToolbar from "../../components/ui/ListToolbar/ListToolbar";

export default function ServidoresCmdPage({ onBack }) {
  const [servidores, setServidores] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastCheck, setLastCheck] = useState(null);
  const [query, setQuery] = useState("");

  const mapResultado = useCallback((resultado) => {
    const lista = Array.isArray(resultado?.servidores)
      ? resultado.servidores
      : [];
    const processados = lista.map((servidor) => ({
      id: servidor.id,
      nome: servidor.name,
      app_url: servidor.app_url,
      server_url: servidor.server_url,
      status: servidor.online ? "online" : "offline",
      error: servidor.error,
      responseTime: servidor.responseTime,
      lastCheck: servidor.lastCheck || null,
    }));
    setServidores(processados);
    setLastCheck(resultado?.lastCheck || null);
  }, []);

  const load = useCallback(
    async (force = false) => {
      try {
        setLoading(true);
        setError(null);

        const resultado = await statusServer({ force });

        if (!resultado?.servidores || resultado.servidores.length === 0) {
          setError("Nenhum servidor disponível para verificação");
          setServidores([]);
          setLastCheck(resultado?.lastCheck || null);
          return;
        }
        mapResultado(resultado);
      } catch (err) {
        console.error("Erro ao carregar status dos servidores:", err);
        setError(
          force
            ? "Falha ao atualizar status dos servidores"
            : "Falha ao carregar status dos servidores"
        );
      } finally {
        setLoading(false);
      }
    },
    [mapResultado]
  );

  useEffect(() => {
    load(false);
  }, [load]);

  const handleRefresh = async () => {
    try {
      setLoading(true);
      await forcarVerificacao();
    } catch (e) {
      console.warn("Falha ao forçar verificação imediata:", e);
    } finally {
      await load(true);
    }
  };

  const subtitle = lastCheck
    ? `Última verificação: ${new Date(lastCheck).toLocaleString()}`
    : "Verifique o status dos servidores do sistema.";

  const filtrados = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return servidores;

    return servidores.filter((s) =>
      [s.nome, s.app_url, s.server_url, s.status].some((v) =>
        String(v || "").toLowerCase().includes(q)
      )
    );
  }, [servidores, query]);

  return (
    <div className="servidores-page">
      <div className="servidores-page__container">
        <PageHeader
          title="Status dos Servidores"
          subtitle={subtitle}
          icon={<Server strokeWidth={1.8} />}
          iconVariant="green"
          rightContent={
            <>
              <button
                type="button"
                className="servidores-refresh-btn"
                onClick={handleRefresh}
                disabled={loading}
              >
                <RefreshCw
                  className={
                    loading
                      ? "servidores-refresh-icon spinning"
                      : "servidores-refresh-icon"
                  }
                  strokeWidth={1.8}
                />
                <span>{loading ? "Atualizando..." : "Atualizar"}</span>
              </button>

              <ListToolbar
                value={query}
                onChange={setQuery}
                searchPlaceholder="Buscar por nome, domínio, servidor ou status..."
                // sem onNew => não mostra botão de "Novo"
              />
            </>
          }
        />

        <div className="servidores-page__grid">
          {loading && (
            <div className="servidores-page__empty">
              Verificando status dos servidores...
            </div>
          )}
          {!loading && error && (
            <div className="servidores-page__empty servidores-page__empty--error">
              {error}
            </div>
          )}
          {!loading && !error && <ServersGrid servidores={filtrados} />}
        </div>
      </div>
    </div>
  );
}